// SPEC-LF1-STORE-LOCAL-CACHE-01: 未送信件数を表示する固定バナー。件数 0 の時は非表示。
// SPEC-LF1-IDEMPOTENT-SYNC-01 D7: タップで未送信レコードの読み取り専用詳細 + 再送ボタン。
// server 側で不可視な stuck record を端末側から診断・救済するため。UI は [A] 系 text-xs 密度。

import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useUnsentBanner } from '../hooks/useUnsentBanner'
import { uploadAllUnsynced } from '../services/storeSync'
import { useAuth } from '../hooks/useAuth'
import { Sentry } from '../lib/sentry'
import { SheetHeader } from '../shared/ui/SheetHeader' // D-1014

// SPEC-PATROL-BADGE-AND-UNSENT-MISFIRE-V1 (D-434) ITEM_5: 詳細モーダルが開いた瞬間の状況を Sentry に1件だけ送る計測。
//   推測で終わらせないため。真偽と経路名のみ(メーター値/ブースコードは送らない)。1セッション1件に限る。
let _d434DiagSent = false
function sendUnsentOpenDiag(extra) {
  if (_d434DiagSent) return
  _d434DiagSent = true
  try {
    Sentry.captureMessage('D-434 unsent-open', { level: 'info', tags: { diag: 'D-434' }, extra })
  } catch { /* 計測失敗で表示を壊さない */ }
}

export default function UnsentBanner() {
  const { count, storeCount, records, refresh } = useUnsentBanner()
  const { staffId } = useAuth()
  const [open, setOpen] = useState(false)
  const [resending, setResending] = useState(false)
  const location = useLocation()
  // ピルは fixed で巡回ブースのスワイプ経路に重なる。スワイプ由来の合成 click で詳細(再送)が誤って開く(ヒロ実機)。
  //   ITEM_4: 座標比較(iOS で合成 click が開始座標を持ち距離0になり信用できない)をやめ、pointermove の実移動で
  //   タップ/スワイプを判別する。pointerdown がピルで発生していない click は常に無視する。
  const downRef = useRef(null)   // {x,y,moved} … アクティブな pointer ジェスチャ(ピル上)
  const tapRef  = useRef(false)  // 直近 pointerup が「ピル上の純タップ」だったか(click が消費)
  const diagRef = useRef({ downOnPill: false, moved: false })

  // ITEM_3(候補A対策): UnsentBanner はアプリ全体に1つで、ブース遷移では再マウントされない。
  //   ルート(pathname)が変わったら詳細モーダルを必ず閉じる=画面が変わっても被さり続ける問題を断つ。
  useEffect(() => { setOpen(false) }, [location.pathname])

  if (!count) return null

  async function handleResend() {
    setResending(true)
    try {
      await uploadAllUnsynced({ staff: { staffId } })
    } catch { /* 失敗は storeSync が logger.error 済み */ }
    setResending(false)
    await refresh()
  }

  return (
    <>
      <button
        type="button"
        data-testid="unsent-banner"
        onPointerDown={e => { downRef.current = { x: e.clientX, y: e.clientY, moved: false }; tapRef.current = false }}
        onPointerMove={e => { const d = downRef.current; if (d && Math.hypot(e.clientX - d.x, e.clientY - d.y) > 10) d.moved = true }}
        onPointerUp={() => {
          const d = downRef.current
          diagRef.current = { downOnPill: !!d, moved: !!(d && d.moved) }
          tapRef.current = !!d && !d.moved       // ピル上で始まり ほぼ動かなかった時だけ純タップ
          downRef.current = null                 // pointerup で状態リセット
        }}
        onPointerCancel={() => { downRef.current = null; tapRef.current = false }}
        onClick={() => {
          const tap = tapRef.current
          tapRef.current = false
          if (!tap) return                       // pointerdown がピルで無い / スワイプ由来の click は無視
          setOpen(prev => {
            const next = !prev
            if (next) sendUnsentOpenDiag({        // ITEM_5: 開いた時だけ1件
              pointerDownOnPill: diagRef.current.downOnPill,
              moved: diagRef.current.moved,
              pathname: location.pathname,
            })
            return next
          })
        }}
        aria-label={`未送信 ${count}件 詳細を開く`}
        // D-1040: 横位置を右端(right-3)→中央へ。390x844 の実測で、右端はランチャーのログアウト(x288-374, y44-72)と
        //   各画面の ? / ホーム(x322-370, y24-88)の中心を塞いでいた(24 画面中 16 要素と重なり)。中央(x123-268, y56-80)は
        //   ヘッダの左(戻る/ホーム x≤122)と右(? x≥298)の間で、重なったのは検索入力 2 つの上端 5〜11px だけ(中心は無事)。
        //   縦は top-14 のまま(集金 top-24 / 容量 top-32 / 継続 top-40 の梯子を崩さない)。left-1/2 は残り幅で折り返すので nowrap。
        className="fixed top-14 left-1/2 -translate-x-1/2 z-[110] px-3 py-1 rounded-full bg-amber-500/90 text-white text-xs font-bold whitespace-nowrap shadow active:scale-95"
      >
        未送信 {count}件 / {storeCount}店舗
      </button>

      {open && (
        <div
          data-testid="unsent-detail"
          className="fixed inset-0 z-[111] flex items-start justify-center bg-black/50 pt-10 px-3"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-md bg-surface border border-border rounded-xl p-3 max-h-[80vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* D-1014: 見出しは SheetHeader(閉じる = 48×48 の x アイコン) */}
            <SheetHeader title={`未送信レコード (${count}件)`} onClose={() => setOpen(false)} closeTestId="unsent-detail-close" className="mb-2" />

            <div className="overflow-y-auto">
              <table className="w-full text-xs" data-testid="unsent-detail-table">
                <thead>
                  <tr className="text-muted border-b border-border">
                    <th className="text-left py-1 pr-1 font-normal">ブース</th>
                    <th className="text-left py-1 px-1 font-normal">日付</th>
                    <th className="text-right py-1 px-1 font-normal">IN</th>
                    <th className="text-right py-1 px-1 font-normal">OUT</th>
                    <th className="text-left py-1 pl-1 font-normal">err</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map(r => (
                    <tr key={r.localId} data-testid="unsent-row" className="border-b border-border/50">
                      <td className="py-1 pr-1 font-mono truncate max-w-[8rem]">{r.booth_code ?? '—'}</td>
                      <td className="py-1 px-1 font-mono">{r.patrol_date ?? '—'}</td>
                      <td className="py-1 px-1 text-right font-mono">{r.in_meter ?? '—'}</td>
                      <td className="py-1 px-1 text-right font-mono">{r.out_meter ?? '—'}</td>
                      <td className="py-1 pl-1 text-[10px] text-accent">{r.lastErrCode ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              type="button"
              data-testid="unsent-resend"
              onClick={handleResend}
              disabled={resending}
              className={`mt-3 w-full py-2 rounded-lg text-sm font-bold ${resending ? 'bg-surface2 text-muted opacity-50' : 'bg-accent text-bg active:scale-[0.98]'}`}
            >
              {resending ? '送信中…' : '再送'}
            </button>
          </div>
        </div>
      )}
    </>
  )
}
